/**
 * React hook for managing Spinner Wheel state
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { parseEntriesFromUrl, updateUrlWithEntries, makeId, debounce } from '../utils/helpers';
import type {
  WheelEntry,
  CurrentSegment,
  UseSpinnerWheelStateOptions,
  UseSpinnerWheelStateReturn,
} from '../types/wheel';

export function useSpinnerWheelState(
  options: UseSpinnerWheelStateOptions = {}
): UseSpinnerWheelStateReturn {
  const { initialEntries, syncToUrl = false, onWinnerSelected } = options;

  // Core state
  const [entries, setEntriesInternal] = useState<WheelEntry[]>(initialEntries || []);

  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<WheelEntry | null>(null);
  const [currentSegment, setCurrentSegment] = useState<CurrentSegment>({
    style: '',
    text: '',
  });
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);

  // Track URL sync initialization
  const isInitializedFromUrl = useRef(false);
  const onWinnerSelectedRef = useRef(onWinnerSelected);
  onWinnerSelectedRef.current = onWinnerSelected;

  // Debounced URL update function
  const debouncedUrlUpdate = useRef(
    debounce((entries: WheelEntry[]) => {
      updateUrlWithEntries(entries);
    }, 300)
  );

  // Load entries from URL on mount
  useEffect(() => {
    if (!syncToUrl || typeof window === 'undefined') return;

    let cancelled = false;
    parseEntriesFromUrl().then((urlEntries) => {
      if (cancelled) return;
      isInitializedFromUrl.current = true;
      if (urlEntries.length > 0) {
        setEntriesInternal(urlEntries);
      }
    });

    return () => { cancelled = true; };
  }, [syncToUrl]);

  // URL sync effect
  useEffect(() => {
    if (!syncToUrl || typeof window === 'undefined') return;

    // Skip URL update until initial load from URL is done
    if (!isInitializedFromUrl.current) return;

    debouncedUrlUpdate.current(entries);
  }, [entries, syncToUrl]);

  // Actions
  const setEntries = useCallback((newEntries: WheelEntry[]) => {
    setEntriesInternal(newEntries);
    setWinner(null);
    setCurrentSegment({ style: '', text: '' });
  }, []);

  const addEntries = useCallback((input: string) => {
    if (!input.trim()) return;

    const newEntries = input
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name) => ({
        id: makeId(),
        name,
      }));

    if (newEntries.length > 0) {
      setEntriesInternal((prev) => [...prev, ...newEntries]);
    }
  }, []);

  const removeEntry = useCallback(
    (index: number) => {
      if (isSpinning) return;

      setEntriesInternal((prev) => prev.filter((_, i) => i !== index));
      setWinner(null);
      setCurrentSegment({ style: '', text: '' });
    },
    [isSpinning]
  );

  const clearEntries = useCallback(() => {
    setEntriesInternal([]);
    setWinner(null);
    setCurrentSegment({ style: '', text: '' });
    setShowWinnerPopup(false);
  }, []);

  const spin = useCallback(() => {
    if (isSpinning || entries.length === 0) return;
    setIsSpinning(true);
  }, [isSpinning, entries.length]);

  const handleSpinComplete = useCallback((winnerEntry: WheelEntry) => {
    setIsSpinning(false);
    setWinner(winnerEntry);
    setShowWinnerPopup(true);
    onWinnerSelectedRef.current?.(winnerEntry);
  }, []);

  const handleSegmentChange = useCallback((segment: CurrentSegment) => {
    setCurrentSegment(segment);
  }, []);

  const closeWinnerPopup = useCallback(() => {
    setShowWinnerPopup(false);
  }, []);

  const reset = useCallback(() => {
    setEntriesInternal(initialEntries || []);
    setIsSpinning(false);
    setWinner(null);
    setCurrentSegment({ style: '', text: '' });
    setShowWinnerPopup(false);
  }, [initialEntries]);

  return {
    // State
    entries,
    isSpinning,
    winner,
    currentSegment,
    showWinnerPopup,

    // Actions
    setEntries,
    addEntries,
    removeEntry,
    clearEntries,
    spin,
    handleSpinComplete,
    handleSegmentChange,
    closeWinnerPopup,
    reset,
  };
}
